<template>
  <Dialog
    :is-open="isOpen"
    :title="$t('comments.title')"
    :close-label="$t('comments.close')"
    variant="drawer"
    @close="emit('close')"
  >
    <!-- Loading state -->
    <div v-if="commentsStore.isLoading(photoId)" class="loading-state">
      <LoadingIcon class="w-8 h-8 text-stone-600 dark:text-stone-400" />
      <p class="text-stone-600 dark:text-stone-400">{{ $t('comments.loading') }}</p>
    </div>

    <!-- Comments list -->
    <div v-else-if="comments.length > 0" class="comments-list">
      <div v-for="comment in comments" :key="comment.id" class="comment-item">
        <RouterLink :to="{ name: 'profile-username', params: { username: comment.username } }">
          <Avatar :avatar="comment.avatar" size="xs" />
        </RouterLink>
        <div class="comment-content">
          <div class="comment-header">
            <RouterLink
              :to="{ name: 'profile-username', params: { username: comment.username } }"
              class="comment-username"
              >{{ comment.username }}</RouterLink
            >
            <time class="comment-time" :datetime="comment.created_at">
              {{ formatTime(comment.created_at) }}
            </time>
          </div>
          <p class="comment-text">{{ comment.content }}</p>
        </div>
        <button
          v-if="canDeleteComment(comment)"
          @click="handleDeleteComment(comment.id)"
          class="delete-button"
          :aria-label="$t('comments.delete')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3,6 5,6 21,6"></polyline>
            <path
              d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"
            ></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          class="w-16 h-16"
        >
          <!-- Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE -->
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092a10 10 0 1 0-4.777-4.719"
          />
        </svg>
      </div>
      <p class="empty-text">{{ $t('comments.no_comments') }}</p>
    </div>

    <template #footer>
      <div class="comment-form">
        <div class="form-row">
          <label for="comment-input" class="sr-only">{{ $t('comments.placeholder') }}</label>
          <textarea
            id="comment-input"
            v-model="newComment"
            :placeholder="$t('comments.placeholder')"
            class="comment-input"
            rows="3"
            maxlength="200"
            @keydown.enter.meta.prevent="handleAddComment"
            @keydown.enter.ctrl.prevent="handleAddComment"
            :aria-describedby="characterCounterId"
          ></textarea>
        </div>
        <div class="form-footer">
          <span
            :id="characterCounterId"
            class="character-counter"
            :class="{ 'over-limit': newComment.length >= 200 }"
            aria-live="polite"
          >
            {{ newComment.length }}/200
          </span>
          <button
            @click="handleAddComment"
            :disabled="!canSubmit"
            class="submit-button"
            :class="{ disabled: !canSubmit }"
            :aria-label="$t('comments.post')"
            type="button"
          >
            {{ $t('comments.post') }}
          </button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useComments, type Comment } from '@/stores/comments'
import { useSupabase } from '@/stores/supabase'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import Avatar from '@/components/Avatar.vue'
import LoadingIcon from '@/components/Icons/LoadingIcon.vue'
import Dialog from '@/components/Dialog.vue'

const { t } = useI18n()
const { showToast } = useToast()
const supabase = useSupabase()
const commentsStore = useComments()

const props = defineProps<{
  isOpen: boolean
  photoId: string
  photoOwnerId: string
}>()

const emit = defineEmits<{
  close: []
  commentAdded: []
  commentCountChanged: [count: number]
}>()

// Generate unique ID for accessibility
const characterCounterId = `character-counter-${Math.random().toString(36).substr(2, 9)}`

const newComment = ref('')
const comments = computed(() => commentsStore.getComments(props.photoId))

const canSubmit = computed(() => {
  return (
    newComment.value.trim().length > 0 &&
    newComment.value.length <= 200 &&
    supabase.user.status === 'SignedIn'
  )
})

const canDeleteComment = (comment: Comment) => {
  if (supabase.user.status !== 'SignedIn') return false

  const currentUserId = supabase.user.data?.id

  // User can delete their own comments
  if (currentUserId === comment.user_id) return true

  // Photo owner can delete any comment on their photo
  if (currentUserId === props.photoOwnerId) return true

  return false
}

const formatTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('comments.time.now')
  if (minutes < 60) return t('comments.time.minutes', { count: minutes })
  if (hours < 24) return t('comments.time.hours', { count: hours })
  if (days < 7) return t('comments.time.days', { count: days })

  return new Intl.DateTimeFormat(t('locale'), {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  }).format(date)
}

const handleAddComment = async () => {
  if (!canSubmit.value) return

  const content = newComment.value.trim()
  const success = await commentsStore.addComment(props.photoId, content)

  if (success) {
    newComment.value = ''
    showToast(t('comments.added_successfully'))
    emit('commentAdded')
    emit('commentCountChanged', comments.value.length)

    // Smooth scroll to bottom to show the new comment
    await nextTick()
    const drawerContent = document.querySelector('.drawer-content')
    if (drawerContent) {
      drawerContent.scrollTo({
        top: drawerContent.scrollHeight,
        behavior: 'smooth'
      })
    }
  } else {
    showToast(t('comments.add_failed'))
  }
}

const handleDeleteComment = async (commentId: string) => {
  if (!window.confirm(t('comments.delete_confirm'))) return

  const success = await commentsStore.deleteComment(commentId, props.photoId, props.photoOwnerId)

  if (success) {
    showToast(t('comments.deleted_successfully'))
    emit('commentAdded') // Trigger comment count refresh
    emit('commentCountChanged', comments.value.length)
  } else {
    showToast(t('comments.delete_failed'))
  }
}

// Fetch comments when drawer opens
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await commentsStore.fetchComments(props.photoId)
      emit('commentCountChanged', comments.value.length)
      await nextTick()
      // Focus the textarea when drawer opens
      const textarea = document.querySelector('.comment-input') as HTMLTextAreaElement
      if (textarea) {
        textarea.focus()
      }
    }
  }
)
</script>

<style scoped>
/* Component-specific styles */

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: theme('spacing.3');
  padding: theme('spacing.8');
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: theme('spacing.4');
}

.comment-item {
  display: flex;
  gap: theme('spacing.3');
  align-items: flex-start;
}

.comment-content {
  flex: 1;
  min-width: 0;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: theme('spacing.2');
  margin-bottom: theme('spacing.1');
}

.comment-username {
  font-weight: theme('fontWeight.semibold');
  color: theme('colors.stone.900');
  font-size: theme('fontSize.sm');

  .dark & {
    color: theme('colors.stone.100');
  }
}

.comment-time {
  font-size: theme('fontSize.xs');
  color: theme('colors.stone.500');

  .dark & {
    color: theme('colors.stone.400');
  }
}

.comment-text {
  color: theme('colors.stone.700');
  font-size: theme('fontSize.sm');
  line-height: theme('lineHeight.relaxed');
  word-wrap: break-word;

  .dark & {
    color: theme('colors.stone.300');
  }
}

.delete-button {
  padding: theme('spacing.1');
  color: theme('colors.stone.400');
  transition: color 0.2s;

  &:hover {
    color: theme('colors.red.500');
  }

  .dark & {
    color: theme('colors.stone.500');

    &:hover {
      color: theme('colors.red.400');
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: theme('spacing.8');
  gap: theme('spacing.3');
}

.empty-icon {
  color: theme('colors.stone.400');

  .dark & {
    color: theme('colors.stone.500');
  }
}

.empty-text {
  color: theme('colors.stone.600');
  font-size: theme('fontSize.sm');
  text-align: center;

  .dark & {
    color: theme('colors.stone.400');
  }
}

.comment-form {
  width: 100%;
}

.form-row {
  margin-bottom: theme('spacing.3');
}

.comment-input {
  width: 100%;
  padding: theme('spacing.3');
  border: 1px solid theme('colors.stone.300');
  border-radius: theme('borderRadius.lg');
  resize: none;
  font-size: theme('fontSize.sm');
  color: theme('colors.stone.900');
  background: theme('colors.white');
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: theme('colors.purple.500');
  }

  .dark & {
    background: theme('colors.stone.700');
    border-color: theme('colors.stone.600');
    color: theme('colors.stone.100');

    &:focus {
      border-color: theme('colors.purple.400');
    }
  }
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.character-counter {
  font-size: theme('fontSize.xs');
  color: theme('colors.stone.500');
  transition: color 0.2s;

  &.over-limit {
    color: theme('colors.red.500');
  }

  .dark & {
    color: theme('colors.stone.400');

    &.over-limit {
      color: theme('colors.red.400');
    }
  }
}

.submit-button {
  padding: theme('spacing.2') theme('spacing.4');
  background: theme('colors.purple.600');
  color: theme('colors.white');
  border-radius: theme('borderRadius.lg');
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.medium');
  transition: all 0.2s;

  &:hover:not(.disabled) {
    background: theme('colors.purple.700');
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .dark & {
    background: theme('colors.purple.500');

    &:hover:not(.disabled) {
      background: theme('colors.purple.600');
    }
  }
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
